import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImagePlus, FileUp, Trash2, Loader2 } from 'lucide-react';
import { Shul } from '../../types';
import {
  useShulImages,
  UploadValidationError,
} from '../../hooks/useShulImages';

interface ShulImagesEditorProps {
  shul: Shul;
}

export default function ShulImagesEditor({ shul }: ShulImagesEditorProps) {
  const { t } = useTranslation();
  const coverInputRef = useRef<HTMLInputElement>(null);
  const postersInputRef = useRef<HTMLInputElement>(null);
  const {
    loading,
    error,
    clearError,
    uploadCover,
    removeCover,
    uploadPoster,
    removePoster,
  } = useShulImages(shul.id);
  const [localError, setLocalError] = useState<string | null>(null);

  const showError = (err: unknown) => {
    if (err instanceof UploadValidationError) {
      setLocalError(t(`admin.${err.message}`));
      return;
    }
    setLocalError(err instanceof Error ? err.message : t('common.error'));
  };

  const handleCoverChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    clearError();
    setLocalError(null);
    try {
      await uploadCover(file, shul.photoUrl);
    } catch (err) {
      showError(err);
    }
  };

  const handleRemoveCover = async () => {
    clearError();
    setLocalError(null);
    try {
      await removeCover(shul.photoUrl);
    } catch (err) {
      showError(err);
    }
  };

  const handlePostersChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    clearError();
    setLocalError(null);
    for (const file of files) {
      try {
        await uploadPoster(file);
      } catch (err) {
        showError(err);
        break;
      }
    }
  };

  const handleRemovePoster = async (posterId: string) => {
    const poster = (shul.posters || []).find((p) => p.id === posterId);
    if (!poster) return;
    if (!window.confirm(t('admin.confirmRemovePoster'))) return;
    clearError();
    setLocalError(null);
    try {
      await removePoster(poster);
    } catch (err) {
      showError(err);
    }
  };

  const displayError = localError || error;

  return (
    <div className="space-y-4 border-t border-ink/10 pt-3">
      <div className="space-y-2">
        <p className="text-sm font-medium text-ink">{t('admin.coverImage')}</p>
        <p className="text-xs text-ash">{t('admin.coverImageHint')}</p>

        {shul.photoUrl ? (
          <div className="space-y-2">
            <img
              src={shul.photoUrl}
              alt={t('admin.coverImage')}
              className="w-full max-h-48 object-cover rounded-[16px] border border-ink/10"
            />
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                disabled={loading}
                onClick={() => coverInputRef.current?.click()}
                className="text-sm text-ink hover:underline disabled:opacity-50"
              >
                {t('admin.replaceCover')}
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={handleRemoveCover}
                className="text-sm text-ink hover:underline disabled:opacity-50"
              >
                {t('admin.removeCover')}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            disabled={loading}
            onClick={() => coverInputRef.current?.click()}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-[10px] border border-ink/20 bg-paper text-sm text-ink hover:border-ink/40 disabled:opacity-50"
          >
            <ImagePlus className="w-4 h-4 shrink-0" />
            {t('admin.uploadCover')}
          </button>
        )}

        <input
          ref={coverInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleCoverChange}
        />
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium text-ink">{t('admin.posters')}</p>
        <p className="text-xs text-ash">{t('admin.postersHint')}</p>

        <ul className="space-y-2">
          {(shul.posters || []).map((poster) => (
            <li
              key={poster.id}
              className="flex items-center gap-3 rounded-[10px] border border-ink/10 bg-paper p-2"
            >
              <img
                src={poster.thumbnailUrl}
                alt={poster.fileName}
                className="w-14 h-14 object-cover rounded-[10px] shrink-0 border border-ink/10"
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-ink truncate">{poster.fileName}</p>
                <p className="text-xs text-ash">
                  {poster.contentType === 'pdf'
                    ? t('admin.posterTypePdf')
                    : t('admin.posterTypeImage')}
                </p>
              </div>
              <button
                type="button"
                disabled={loading}
                onClick={() => handleRemovePoster(poster.id)}
                className="shrink-0 p-1.5 rounded-[10px] text-ash hover:text-ink hover:bg-fog disabled:opacity-50"
                aria-label={t('admin.removePoster')}
                title={t('admin.removePoster')}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>

        <button
          type="button"
          disabled={loading}
          onClick={() => postersInputRef.current?.click()}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-[10px] border border-ink/20 bg-paper text-sm text-ink hover:border-ink/40 disabled:opacity-50"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 shrink-0 animate-spin" />
          ) : (
            <FileUp className="w-4 h-4 shrink-0" />
          )}
          {t('admin.uploadPosters')}
        </button>

        <input
          ref={postersInputRef}
          type="file"
          accept="image/*,application/pdf,.pdf"
          multiple
          className="hidden"
          onChange={handlePostersChange}
        />
      </div>

      {displayError && (
        <p className="text-sm text-danger-600" role="alert">
          {displayError}
        </p>
      )}
    </div>
  );
}
